/** Section 3.4 - what one player is allowed to know.
 *
 * The rule this file exists to enforce (I9): nothing in the returned object may
 * describe a cell the player's fog marks `UNSEEN`. Dense planes are masked to
 * zero outside explored ground, and an enemy asset appears either as itself
 * (because it is lit right now) or as the stale snapshot the player last saw -
 * never as its current truth on unexplored ground.
 */

import type { BattleState } from "../battle/state.js";
import { SIDE_ATTACKER, SIDE_DEFENDER } from "../battle/state.js";
import type {
  ArmyState, CaravanState, FogVisibility, GameState, HeroState, MapObjectKind,
  MapObjectState, PendingInteraction, PlayerState, PrimarySkills, Resources, TownState,
  TurnState,
} from "../state.js";
import {
  asCreatureDefId,
  type ArmyId, type CreatureDefId, type HeroId, type ObjectId, type PlayerId, type TownId,
} from "../ids.js";
import { cellIndex } from "../state.js";
import { FOG_UNSEEN, FOG_VISIBLE, type FogSnapshot } from "../map/fog.js";
import { entityKeys } from "../util/iter.js";
import { disguisedLevel, primaryFor } from "./hero.js";
import { grailCell, obeliskProgress } from "./grail.js";
import { makeCtx, type Ctx } from "./ctx.js";
import { playerMarkKey } from "./payload.js";
import { hiddenFrom } from "./stealth.js";
import { explainPlayerIncome } from "./turn.js";
import { RESOURCES } from "../state.js";
import type { ContentRegistry } from "../content/registry.js";
import { cellCoords } from "../map/index.js";

export type Viewer = PlayerId | "spectator";

export interface ObservedMap {
  readonly width: number;
  readonly height: number;
  readonly levels: number;
  /** Zero outside explored ground. */
  readonly terrain: Uint8Array;
  readonly variant: Uint8Array;
  readonly road: Uint8Array;
  readonly river: Uint8Array;
  /** Section 31: the magic-ground slot per cell, zero outside explored ground. */
  readonly ground: Uint8Array;
  /** What those slots mean; the map's own legend, copied whole. */
  readonly groundIndex: readonly (string | null)[];
  readonly moveCost: Uint16Array;
  /** 0 unseen, 1 remembered, 2 visible. */
  readonly visibility: Uint8Array;
}

export interface ObservedHero {
  readonly id: HeroId;
  readonly def: string;
  readonly owner: PlayerId;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly facing: number;
  readonly level: number;
  /**
   * Resolved primaries (base + artifacts + specialty + kingdom). Own heroes
   * only; null for an enemy, matching `detail`.
   *
   * `detail.primary` is the stored base. The sheet, the info card and the
   * kingdom list must print this field, because a Wyrmfang Crown is +4
   * Knowledge and printing the stored 3 is how the crown looked like it did
   * nothing.
   */
  readonly primary: PrimarySkills | null;
  /** Own heroes only; an enemy's sheet is not visible from the map. */
  readonly detail: HeroState | null;
  readonly army: ArmyState | null;
}

export interface ObservedTown {
  readonly id: TownId;
  readonly faction: string;
  readonly owner: PlayerId | null;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly detail: TownState | null;
  readonly garrison: ArmyState | null;
}

export interface ObservedObject {
  readonly id: ObjectId;
  readonly kind: MapObjectKind;
  readonly def: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Null when the object is only remembered, so ownership cannot be scouted. */
  readonly owner: PlayerId | null;
  readonly visible: boolean;
  /**
   * True when this player has already taken what the object gives once per
   * player - the `p:<player>` mark the payload carries.
   *
   * It is the player's own memory of his own visit, so no fog question arises:
   * you know where you have been. It is reported for a remembered object as
   * happily as for a lit one for the same reason.
   *
   * The planner needs it because an obelisk is not consumed and not removed. It
   * stands there afterwards looking exactly like one nobody has read, and
   * without this the only two options are to ignore obelisks entirely (which is
   * what the AI did) or to walk the same one every day forever.
   */
  readonly marked: boolean;
  /**
   * How many creatures an external dwelling has waiting, for its OWNER only.
   *
   * Null for everything else - another kind of object, somebody else's
   * dwelling, one that is merely remembered. A stock is instance state and the
   * observation carries no payloads, so without this the only thing a planner
   * could do with a dwelling is assume its def's *starting* stock, which is
   * true for exactly as long as nobody has bought from it.
   *
   * That assumption is what made the AI issue caravans the kernel refused: it
   * read an emptied dwelling as still holding a week's worth. A player can see
   * what is standing in a dwelling they own, so reporting it here is not a leak
   * - and it is deliberately not reported for one they do not own, which is
   * the same line ownership itself is drawn on two fields above.
   */
  readonly stock: number | null;
  /**
   * The stack standing here, for a wandering monster. Null on every other kind.
   *
   * A payload is what the fog hides, but a wandering stack's identity is the
   * sprite: once you have seen it you know what it is. The generator writes
   * any combat creature into the instance payload and reuses one of nineteen
   * stand-in sprites, so without this field the plate would name the sprite
   * ("Imps") and the kernel would fight the payload ("Spearwardens").
   *
   * Count is the size of that stack. The classic approximates it with the
   * few/several/horde ladder; this observation is also what the planner prices
   * a fight against, so the number the generator wrote is the one that is
   * reported. The hover plate does not have to print it.
   */
  readonly creature: CreatureDefId | null;
  readonly creatureCount: number | null;
}

export interface ObservedOpponent {
  readonly id: PlayerId;
  readonly colour: string;
  readonly kind: PlayerState["kind"];
  readonly defeated: boolean;
  /** Counts only; a fog-limited view still shows the scoreboard. */
  readonly heroCount: number;
  readonly townCount: number;
}

/**
 * What the obelisks have bought this player, without the picture.
 *
 * The full `PuzzleView` carries a terrain plane the size of the map, which is
 * the right thing to hand a screen once and the wrong thing to allocate on
 * every observation - and a planner has no use for the picture anyway. What it
 * needs is how far along the search is and, once the search is over, where to
 * put the shovel.
 *
 * Null when `observe` was called without a content registry: deciding whether
 * an object is an obelisk is a question about its DEFINITION, and the two
 * callers that do not pass content are tests that do not ask about the Grail.
 */
export interface ObservedPuzzle {
  readonly seen: number;
  readonly total: number;
  /** The buried cell, or -1 until every obelisk has been stood on. */
  readonly cell: number;
  readonly x: number;
  readonly y: number;
}

export interface PlayerObservation {
  readonly version: number;
  readonly contentVersion: string;
  readonly viewer: Viewer;
  readonly turn: TurnState;
  readonly isActive: boolean;
  readonly self: PlayerState | null;
  readonly resources: Resources | null;
  /**
   * What tomorrow pays, per resource: towns, flagged mines and everything the
   * heroes carry (Stewardship, a resource specialty, a class passive). Computed by
   * the kernel because only the kernel can resolve a hero's stats; the UI used
   * to sum town income alone and under-report every kingdom with a mine. Null
   * for a spectator or when no content was handed in.
   */
  readonly income: Resources | null;
  readonly map: ObservedMap;
  readonly heroes: readonly ObservedHero[];
  readonly towns: readonly ObservedTown[];
  readonly objects: readonly ObservedObject[];
  readonly armies: Readonly<Record<string, ArmyState>>;
  /**
   * The viewer's own columns on the road, and nobody else's.
   *
   * A caravan is not on the map: it has no cell to light and no footprint to
   * see, so fog has no opinion about it and the usual "is this lit?" test does
   * not apply. What decides it is ownership - a player knows what he dispatched
   * and cannot know what a rival did - so this is filtered by owner rather than
   * by visibility, and a spectator sees them all.
   *
   * It exists because a plan that cannot see them reads a town as weaker than
   * it will be in two days, and buys the same reinforcements twice.
   */
  readonly caravans: readonly CaravanState[];
  /** Obelisk progress and, once solved, the dig cell. See `ObservedPuzzle`. */
  readonly puzzle: ObservedPuzzle | null;
  /** Last-seen snapshots, keyed as in `FogState.memory`. Stale by design. */
  readonly remembered: Readonly<Record<string, FogSnapshot>>;
  readonly opponents: readonly ObservedOpponent[];
  readonly pending: PendingInteraction | null;
  readonly battle: BattleState | null;
  readonly victory: GameState["victory"];
}

const EMPTY_MEMORY: Readonly<Record<string, FogSnapshot>> = Object.freeze({});

/**
 * What an external dwelling is holding right now.
 *
 * The instance payload first and the def's starting value second, which is the
 * same order `payloadOf` merges them in - a dwelling that has never been
 * touched carries no `stock` of its own and holds what its def says.
 */
function dwellingStock(o: MapObjectState): number | null {
  const own = o.payload["stock"];
  if (typeof own === "number") return own;
  return null;
}

function wandererOf(
  o: MapObjectState, content: ContentRegistry | undefined,
): { creature: CreatureDefId | null; creatureCount: number | null } {
  if (o.kind !== "monster") return { creature: null, creatureCount: null };
  const def = content?.mapObjects.get(o.def);
  const instC = o.payload["creature"];
  const defC = def?.payload["creature"];
  const creature = typeof instC === "string" ? asCreatureDefId(instC)
    : typeof defC === "string" ? asCreatureDefId(defC)
      : null;
  const instN = o.payload["count"];
  const defN = def?.payload["count"];
  const creatureCount = typeof instN === "number" ? instN
    : typeof defN === "number" ? defN
      : null;
  return { creature, creatureCount };
}

function kingdomIncome(state: GameState, p: PlayerId, content: ContentRegistry): Resources {
  const c = makeCtx(state, content);
  const out: Record<string, number> = {};
  for (const r of RESOURCES) out[r] = explainPlayerIncome(c, p, r);
  return out as unknown as Resources;
}

export function observe(
  state: GameState, viewer: Viewer, content?: ContentRegistry,
): PlayerObservation {
  const spectator = viewer === "spectator";
  const fog = spectator ? null : state.fog[viewer] ?? null;
  const m = state.map;
  const n = m.width * m.height * m.levels;

  const visibility = new Uint8Array(n);
  if (fog === null) visibility.fill(FOG_VISIBLE);
  else visibility.set(fog.seen.subarray(0, n));

  const terrain = new Uint8Array(n);
  const variant = new Uint8Array(n);
  const road = new Uint8Array(n);
  const river = new Uint8Array(n);
  const ground = new Uint8Array(n);
  const moveCost = new Uint16Array(n);
  for (let i = 0; i < n; i++) {
    if (visibility[i] === FOG_UNSEEN) continue;
    terrain[i] = m.terrain[i]!;
    variant[i] = m.variant[i]!;
    road[i] = m.road[i]!;
    river[i] = m.river[i]!;
    ground[i] = m.ground[i] ?? 0;
    moveCost[i] = m.moveCost[i]!;
  }

  const at = (x: number, y: number, z: number): FogVisibility => {
    if (x < 0 || y < 0 || z < 0 || x >= m.width || y >= m.height || z >= m.levels) {
      return FOG_UNSEEN;
    }
    return (visibility[cellIndex(m, x, y, z)] ?? FOG_UNSEEN) as FogVisibility;
  };

  const armies: Record<string, ArmyState> = {};
  const keepArmy = (id: ArmyId | null): ArmyState | null => {
    if (id === null) return null;
    const a = state.armies[id];
    if (a === undefined) return null;
    armies[id as string] = a;
    return a;
  };

  // One `Ctx` for the whole call, shared with the puzzle below. `makeCtx` is a
  // three-field literal and everything it is handed to here reads and does not
  // write, so this is the shape those helpers take rather than a rule running.
  const ctx = content === undefined ? null : makeCtx(state, content);

  const heroes: ObservedHero[] = [];
  for (const id of entityKeys(state.heroes)) {
    const h = state.heroes[id]!;
    const own = spectator || h.owner === viewer;
    if (!own && at(h.x, h.y, h.z) !== FOG_VISIBLE) continue;
    // Stealth. Deliberately AFTER the fog test - it is the more expensive
    // question and it only ever applies to a hero that is otherwise lit - and
    // skipped entirely without a registry, so an observation built without one
    // behaves exactly as it did before stealth existed.
    if (!own && ctx !== null && content !== undefined
        && hiddenFrom(ctx, content, h, viewer as PlayerId)) continue;
    heroes.push({
      id: h.id, def: h.def as string, owner: h.owner,
      x: h.x, y: h.y, z: h.z, facing: h.facing,
      // Disguise hides the caster's strength from everyone but themselves
      // (Section 30); their own sheet always reads true.
      level: own ? h.level : disguisedLevel(h),
      primary: !own ? null : ctx === null ? h.primary : primaryFor(ctx, h),
      detail: own ? h : null,
      army: own ? keepArmy(h.army) : null,
    });
  }

  const towns: ObservedTown[] = [];
  for (const id of entityKeys(state.towns)) {
    const t = state.towns[id]!;
    const own = spectator || t.owner === viewer;
    // A town is a landmark: once its cell is explored the player knows it is
    // there. Its interior, and who holds it now, need current sight.
    const vis = at(t.x, t.y, t.z);
    if (!own && vis === FOG_UNSEEN) continue;
    const lit = own || vis === FOG_VISIBLE;
    towns.push({
      id: t.id, faction: t.faction as string,
      owner: lit ? t.owner : null,
      name: t.name, x: t.x, y: t.y, z: t.z,
      detail: own ? t : null,
      garrison: own ? keepArmy(t.garrison) : null,
    });
  }

  const objects: ObservedObject[] = [];
  for (const id of entityKeys(state.objects)) {
    const o = state.objects[id]!;
    const vis = at(o.x, o.y, o.z);
    if (vis === FOG_UNSEEN) continue;
    if (o.removed && vis !== FOG_VISIBLE) continue;
    if (o.removed) continue;
    const lit = spectator || vis === FOG_VISIBLE;
    objects.push({
      id: o.id, kind: o.kind, def: o.def,
      x: o.x, y: o.y, z: o.z,
      owner: lit ? o.owner : null,
      visible: lit,
      // Spectator counts as owner here for the same reason it does above: a
      // spectator sees everything, and a null would make the debug view blinder
      // than a player.
      // Spectator has no mark of its own and nothing to remember, so it is told
      // the truth it would see on screen: nobody's visit is its visit.
      marked: !spectator && o.payload[playerMarkKey(viewer)] === true,
      stock: o.kind === "dwelling" && (spectator || (lit && o.owner !== null && o.owner === viewer))
        ? dwellingStock(o)
        : null,
      ...wandererOf(o, content),
    });
  }

  // Owner-filtered, not fog-filtered: see the field's note. `entityKeys` keeps
  // the order stable, which matters because a planner reads this as a list.
  const caravans: CaravanState[] = [];
  for (const id of entityKeys(state.caravans)) {
    const cv = state.caravans[id]!;
    if (!spectator && cv.owner !== viewer) continue;
    caravans.push(cv);
  }

  // The Grail search, for a real player only. A spectator is not searching, and
  // without a registry there is no way to tell an obelisk from a signpost.
  const puzzle: ObservedPuzzle | null = spectator || content === undefined || ctx === null
    ? null
    : puzzleProgress(ctx, viewer);

  const opponents: ObservedOpponent[] = [];
  for (const id of entityKeys(state.players)) {
    if (!spectator && id === viewer) continue;
    const p = state.players[id]!;
    opponents.push({
      id: p.id, colour: p.colour, kind: p.kind, defeated: p.defeated,
      heroCount: p.heroes.length, townCount: p.towns.length,
    });
  }

  const self = spectator ? null : state.players[viewer] ?? null;
  const pending = state.pending !== null
    && (spectator || state.pending.playerId === viewer) ? state.pending : null;

  const b = state.battle;
  const inBattle = b !== null && (spectator
    || b.heroes[SIDE_ATTACKER].owner === viewer
    || b.heroes[SIDE_DEFENDER].owner === viewer);

  return {
    version: state.version,
    contentVersion: state.contentVersion,
    viewer,
    turn: state.turn,
    isActive: !spectator && state.turn.activePlayer === viewer,
    self,
    resources: self?.resources ?? null,
    income: self === null || content === undefined ? null : kingdomIncome(state, viewer as PlayerId, content),
    map: {
      width: m.width, height: m.height, levels: m.levels,
      terrain, variant, road, river, ground, groundIndex: m.groundIndex,
      moveCost, visibility,
    },
    heroes,
    towns,
    objects,
    armies,
    caravans,
    puzzle,
    remembered: fog?.memory ?? EMPTY_MEMORY,
    opponents,
    pending,
    battle: inBattle ? b : null,
    victory: state.victory,
  };
}


/**
 * `obeliskProgress` plus the dig cell, and the cell only once it is earned.
 *
 * Building a `Ctx` here is not a rule running: `makeCtx` is a three-field
 * literal and both callees read `c.s` and `c.content` and write nothing. It is
 * the shape those two functions take, and duplicating them against a raw state
 * would be two more places to keep in step with what an obelisk is.
 *
 * `grailCell` scans the surface, so it is asked ONLY when the puzzle is solved
 * - which is late, rare, and exactly when a player would look.
 */
function puzzleProgress(c: Ctx, viewer: PlayerId): ObservedPuzzle {
  const { seen, total } = obeliskProgress(c, viewer);
  if (total <= 0 || seen < total) return { seen, total, cell: -1, x: -1, y: -1 };
  const cell = grailCell(c.s);
  if (cell < 0) return { seen, total, cell: -1, x: -1, y: -1 };
  const [x, y] = cellCoords(c.s.map, cell);
  return { seen, total, cell, x: x ?? -1, y: y ?? -1 };
}
